import { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import { CustomerAutocomplete } from '@/components/ui/customer-autocomplete';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { FieldList } from '@/components/ui/field-list';
import { FormSwitch } from '@/components/ui/form-switch';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { CustomerVehicleAutocomplete } from '@/components/ui/customer-vehicle-autocomplete';
import type { VehicleViewModel } from '@/graphql/generated';
import { useCurrentFacility } from '@/hooks/useCurrentFacility';
import { useEntityForm, type EntityFormProps } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

import {
	buildContractFormSubmitData,
	contractFormSchema,
	defaultContractCardItem,
	defaultContractFormValues,
	type ContractFormSubmitData,
	type ContractFormValues
} from './contract-input';

interface CustomerOption {
	customerId: string;
	firmName: string;
	categoryName?: string | null;
	categoryLimit?: number | null;
	contactName?: string | null;
	contactEmail?: string | null;
	contactPhone?: string | null;
}

interface ContractFormProps
	extends EntityFormProps<ContractFormValues, ContractFormSubmitData> {
	customerOptions: CustomerOption[];
	initialCustomerLabel?: string;
	headerTitle: string;
	onBack?: () => void;
	contractNumber?: string;
}

export const ContractForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	customerOptions,
	initialCustomerLabel,
	headerTitle,
	onBack,
	contractNumber
}: ContractFormProps) => {
	const { t } = useTranslation('contracts');
	const { facilityId: currentFacilityId } = useCurrentFacility();

	const form = useEntityForm<ContractFormValues>({
		mode,
		schema: contractFormSchema,
		defaultValues: defaultContractFormValues,
		initialData
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const defaultVehicleItem = useMemo(
		() => ({ vehicleId: '', plateNumber: '', isDeleted: false }),
		[]
	);

	const defaultVipVehicleItem = useMemo(
		() => ({ vehicleId: '', plateNumber: '', isDeleted: false }),
		[]
	);

	const selectedCustomerId = form.watch('customerId');
	const includeAllCustomerVehicles = form.watch('includeAllCustomerVehicles');

	// Reset vehicles and VIP vehicles when customer changes
	const prevCustomerIdRef = useRef(selectedCustomerId);
	useEffect(() => {
		if (prevCustomerIdRef.current !== selectedCustomerId) {
			prevCustomerIdRef.current = selectedCustomerId;
			// Clear explicit vehicles and VIP vehicles
			form.setValue('vehicles', []);
			form.setValue('vipVehicles', []);
		}
	}, [selectedCustomerId, form]);

	const selectedCustomer = useMemo(
		() => customerOptions.find(c => c.customerId === selectedCustomerId),
		[customerOptions, selectedCustomerId]
	);

	const handleSubmit = (values: ContractFormValues) => {
		const payload = buildContractFormSubmitData(values, currentFacilityId);
		return onSubmit(payload);
	};

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="contract-form"
					/>
				}
			/>
			<Form {...form}>
				<form
					id="contract-form"
					onSubmit={form.handleSubmit(handleSubmit, errors => {
						console.log('Form validation errors:', errors);
					})}
					className="space-y-6"
				>
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.sections.contractDetails.title')}
							</h2>
							<p className="text-sm text-muted-foreground">
								{t('form.sections.contractDetails.description')}
							</p>
						</div>

						{contractNumber && (
							<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
								<div>
									<label className="text-sm font-medium leading-none">
										{t('form.contractNumber')}
									</label>
									<Input
										value={contractNumber}
										readOnly
										disabled
										className="mt-2 font-mono text-sm bg-muted"
									/>
								</div>
							</div>
						)}

						<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
							<FormField
								control={form.control}
								name="customerId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.customer')}
										</FormLabel>
										<FormControl>
											<CustomerAutocomplete
												value={field.value}
												onChange={customerId =>
													field.onChange(customerId)
												}
												placeholder={t(
													'form.searchCustomers'
												)}
												className="w-full"
												initialSelectedLabel={
													initialCustomerLabel ||
													selectedCustomer?.firmName ||
													undefined
												}
												autoFocus={mode === 'create'}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="beginDate"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.beginDate')}
										</FormLabel>
										<FormControl>
											<DateInput
												value={field.value}
												onChange={field.onChange}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="endDate"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.endDate')}
										</FormLabel>
										<FormControl>
											<DateInput
												value={field.value}
												onChange={field.onChange}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.monthlyAmount')}
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min="0"
												step="0.01"
												value={field.value}
												onChange={e =>
													field.onChange(
														e.target.value
													)
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="simultaneousVehicleLimit"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.simultaneousVehicleLimit')}
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min="0"
												value={field.value ?? ''}
												onChange={e =>
													field.onChange(
														e.target.value
													)
												}
												placeholder={t(
													'form.vehicleLimit'
												)}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="reservedParkingSlots"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.reservedParkingSlots')}
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min="0"
												value={field.value ?? ''}
												onChange={e =>
													field.onChange(
														e.target.value
													)
												}
												placeholder={t(
													'form.reservedParkingSlotsPlaceholder'
												)}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="preferredLanguage"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.preferredLanguage')}
										</FormLabel>
										<FormControl>
											<SentryDropdown
												value={field.value ?? ''}
												onChange={field.onChange}
												options={[
													{
														value: '',
														label: t(
															'form.preferredLanguageNotSpecified'
														)
													},
													{
														value: 'EL',
														label: t(
															'form.preferredLanguageEL'
														)
													},
													{
														value: 'EN',
														label: t(
															'form.preferredLanguageEN'
														)
													}
												]}
												searchable={false}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
							<FormSwitch
								control={form.control}
								name="isAutoRenewed"
								variant="input"
								label={t('form.autoRenew')}
								enabledText={t('form.autoRenewEnabled')}
								disabledText={t('form.autoRenewDisabled')}
							/>
						</div>
					</Card>

					{/* Vehicles */}
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t(
									'form.sections.vehicleCoverage.title',
									'Vehicle Coverage'
								)}
							</h2>
							<p className="text-sm text-muted-foreground">
								{t(
									'form.sections.vehicleCoverage.description',
									'Define which vehicles are covered by this contract.'
								)}
							</p>
						</div>

						<div className="grid gap-4 grid-cols-1 md:grid-cols-2">
							<FormSwitch
								control={form.control}
								name="includeAllCustomerVehicles"
								variant="input"
								label={t(
									'form.includeAllVehicles',
									'Include All Customer Vehicles'
								)}
								enabledText={t(
									'form.includeAllVehiclesEnabled',
									'All customer vehicles are covered (current and future)'
								)}
								disabledText={t(
									'form.includeAllVehiclesDisabled',
									'Only explicitly listed vehicles are covered'
								)}
							/>
						</div>

						{/* Explicit Vehicles - only when includeAllCustomerVehicles = false */}
						{!includeAllCustomerVehicles && (
							<FieldList
								form={form}
								name="vehicles"
								variant="single-panel"
								singlePanelLayout="inline-wrap"
								singlePanelItemWidthClassName="w-[360px] max-w-full"
								title={t(
									'form.assignedVehicles',
									'Assigned Vehicles'
								)}
								description={t(
									'form.assignedVehiclesDescription',
									'Vehicles explicitly included in this contract.'
								)}
								emptyMessage="No vehicles assigned yet. Add a vehicle to explicitly include it."
								idField="vehicleId"
								defaultItem={defaultVehicleItem}
								addButtonLabel={t(
									'form.addVehicle',
									'Add Vehicle'
								)}
								hideDeleteButton
								fields={[
									{
										name: 'vehicleId',
										isKeyField: true,
										render: ({ form: f, path, onRemove }) => {
											const plateNumber = f.watch(
												`${path}.plateNumber` as any
											);
											return (
												<FormField
													control={f.control}
													name={
														`${path}.vehicleId` as any
													}
													render={({ field }) => (
														<FormItem className="space-y-1">
															<FormLabel className="sr-only">
																{t(
																	'form.vehicle',
																	'Vehicle'
																)}
															</FormLabel>
															<FormControl>
																<CustomerVehicleAutocomplete
																	customerId={
																		selectedCustomerId
																	}
																	value={
																		field.value
																	}
																	onChange={(
																		vehicleId: string,
																		vehicle: VehicleViewModel | null
																	) => {
																		field.onChange(
																			vehicleId
																		);
																		f.setValue(
																			`${path}.plateNumber` as any,
																			vehicle?.plateNumber ??
																				''
																		);
																	}}
																	placeholder={t(
																		'form.selectVehicle'
																	)}
																	initialSelectedLabel={
																		plateNumber ||
																		undefined
																	}
																	onDelete={onRemove}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											);
										}
									}
								]}
							/>
						)}

						{/* VIP Vehicles */}
						<FieldList
							form={form}
							name="vipVehicles"
							variant="single-panel"
							singlePanelLayout="inline-wrap"
							singlePanelItemWidthClassName="w-[360px] max-w-full"
							title={t('form.vipVehicles', 'VIP Vehicles')}
							description={t(
								'form.vipVehiclesDescription',
								'Vehicles with VIP status. VIP is independent of vehicle coverage mode.'
							)}
							emptyMessage="No VIP vehicles assigned."
							idField="vehicleId"
							defaultItem={defaultVipVehicleItem}
							addButtonLabel={t(
								'form.addVipVehicle',
								'Add VIP Vehicle'
							)}
							hideDeleteButton
							fields={[
								{
									name: 'vehicleId',
									isKeyField: true,
									render: ({ form: f, path, onRemove }) => {
										const plateNumber = f.watch(
											`${path}.plateNumber` as any
										);
										return (
											<FormField
												control={f.control}
												name={
													`${path}.vehicleId` as any
												}
												render={({ field }) => (
													<FormItem className="space-y-1">
														<FormLabel className="sr-only">
															{t(
																'form.vipVehicle',
																'VIP Vehicle'
															)}
														</FormLabel>
														<FormControl>
															<CustomerVehicleAutocomplete
																customerId={
																	selectedCustomerId
																}
																value={
																	field.value
																}
																onChange={(
																	vehicleId: string,
																	vehicle: VehicleViewModel | null
																) => {
																	field.onChange(
																		vehicleId
																	);
																	f.setValue(
																		`${path}.plateNumber` as any,
																		vehicle?.plateNumber ??
																			''
																	);
																}}
																placeholder={t(
																	'form.selectVipVehicle',
																	'Select a VIP vehicle...'
																)}
																initialSelectedLabel={
																	plateNumber ||
																	undefined
																}
																onDelete={onRemove}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										);
									}
								}
							]}
						/>
					</Card>

					{/* Contract Cards */}
					<div className="space-y-4">
						<FieldList
							form={form}
							name="cards"
							title={t('form.contractCards', 'Access Cards')}
							description={t(
								'form.contractCardsDescription',
								'Physical access cards linked to this contract.'
							)}
							emptyMessage="No access cards assigned."
							idField="cardKey"
							defaultItem={defaultContractCardItem}
							addButtonLabel={t('form.addCard', 'Add Card')}
							fields={[
								{
									name: 'cardType',
									render: ({ form: f, path }) => (
										<FormField
											control={f.control}
											name={`${path}.cardType` as any}
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{t(
															'form.cardType',
															'Card Type'
														)}
													</FormLabel>
													<FormControl>
														<SentryDropdown
															value={
																field.value ??
																'RFID'
															}
															onChange={
																field.onChange
															}
															options={[
																{
																	value: 'RFID',
																	label: 'RFID'
																},
																{
																	value: 'BARCODE',
																	label: t(
																		'form.cardTypeBarcode',
																		'Barcode'
																	)
																},
																{
																	value: 'MAGNETIC',
																	label: t(
																		'form.cardTypeMagnetic',
																		'Magnetic'
																	)
																}
															]}
															searchable={false}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)
								},
								{
									name: 'cardKey',
									isKeyField: true,
									render: ({ form: f, path }) => (
										<FormField
											control={f.control}
											name={`${path}.cardKey` as any}
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{t(
															'form.cardKey',
															'Card Key / Number'
														)}
													</FormLabel>
													<FormControl>
														<Input
															{...field}
															value={
																field.value ??
																''
															}
															placeholder={t(
																'form.cardKeyPlaceholder',
																'Enter the card identifier...'
															)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)
								},
								{
									name: 'cardStatusId',
									render: ({ form: f, path }) => (
										<FormField
											control={f.control}
											name={`${path}.cardStatusId` as any}
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t('form.cardStatus', 'Status')}</FormLabel>
													<FormControl>
														<SentryDropdown
															value={field.value ?? 'ACTIVE'}
															onChange={field.onChange}
															options={[
																{ value: 'ACTIVE', label: t('form.cardStatusActive', 'Active') },
																{ value: 'SUSPENDED', label: t('form.cardStatusSuspended', 'Suspended') },
																{ value: 'RETURNED', label: t('form.cardStatusReturned', 'Returned') },
																{ value: 'LOST', label: t('form.cardStatusLost', 'Lost') },
																{ value: 'REPLACED', label: t('form.cardStatusReplaced', 'Replaced') },
																{ value: 'DEACTIVATED', label: t('form.cardStatusDeactivated', 'Deactivated') },
																{ value: 'EXPIRED', label: t('form.cardStatusExpired', 'Expired') },
															]}
															placeholder={t('form.cardStatusPlaceholder', 'Select status')}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)
								}
							]}
						/>
					</div>
				</form>
			</Form>
		</>
	);
};
